import { useEffect } from 'react';
import { useAuth, useOrganization } from '@clerk/clerk-react';
import { io } from 'socket.io-client';
import { getSocket, setSocket } from '../lib/socket';
import { useSocketStore } from '../stores/socket.store';
import { queryClient } from '../lib/api';

export { getSocket };

const SOCKET_URL =
  (import.meta.env.VITE_SOCKET_URL as string) ||
  (import.meta.env.VITE_API_URL as string) ||
  'https://api.servegrace.com';

export function useSocketInit() {
  const { getToken, userId } = useAuth();
  const { organization } = useOrganization();
  const { setConnected, addEvent } = useSocketStore();

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;

    async function connect() {
      const token = await getToken();
      if (cancelled) return;

      const socket = io(SOCKET_URL, {
        auth: { token, orgId: organization?.id },
        transports: ['websocket'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
      });

      setSocket(socket);

      socket.on('connect', () => {
        if (!cancelled) setConnected(true);
        console.log('[socket] connected ✓');
      });

      socket.on('disconnect', (reason) => {
        if (!cancelled) setConnected(false);
        console.warn('[socket] disconnected:', reason);
      });

      socket.on('connect_error', (err) => {
        console.error('[socket] connect_error:', err.message, err);
      });

      // Invalidate react-query caches on relevant real-time events
      socket.on('conversation:new', (payload: any) => {
        addEvent({ id: crypto.randomUUID(), type: 'conversation:new', payload, receivedAt: Date.now() });
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
      });

      socket.on('conversation:updated', (payload: any) => {
        addEvent({ id: crypto.randomUUID(), type: 'conversation:updated', payload, receivedAt: Date.now() });
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
        if (payload?.id) {
          queryClient.invalidateQueries({ queryKey: ['conversation', payload.id] });
        }
      });

      socket.on('message:new', (payload: any) => {
        addEvent({ id: crypto.randomUUID(), type: 'message:new', payload, receivedAt: Date.now() });
        if (payload?.conversation_id) {
          queryClient.invalidateQueries({ queryKey: ['messages', payload.conversation_id] });
        }
      });

      socket.on('ticket:updated', (payload: any) => {
        addEvent({ id: crypto.randomUUID(), type: 'ticket:updated', payload, receivedAt: Date.now() });
        queryClient.invalidateQueries({ queryKey: ['tickets'] });
        if (payload?.id) {
          queryClient.invalidateQueries({ queryKey: ['ticket', payload.id] });
        }
      });
    }

    connect().catch(console.error);

    return () => {
      cancelled = true;
      const socket = getSocket();
      if (socket) {
        socket.disconnect();
        setConnected(false);
      }
    };
  }, [userId, organization?.id]);
}
